import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { api } from '../App';
import TTSBar from './TTSBar';

const FORMATS_AUDIO = ['MP3', 'WAV', 'OGG', 'FLAC', 'AAC'];
const FORMATS_VIDEO = ['MP4 360p', 'MP4 480p', 'MP4 720p', 'MP4 1080p'];

const LANGUAGES = [
  { code: 'fr', label: '🇫🇷 FR', name: 'Français' },
  { code: 'en', label: '🇬🇧 EN', name: 'English' },
  { code: 'es', label: '🇪🇸 ES', name: 'Español' },
  { code: 'it', label: '🇮🇹 IT', name: 'Italiano' },
  { code: 'de', label: '🇩🇪 DE', name: 'Deutsch' },
  { code: 'pt', label: '🇵🇹 PT', name: 'Português' },
  { code: 'pl', label: '🇵🇱 PL', name: 'Polski' },
  { code: 'creole', label: '🌺 KRY', name: 'Créole' },
  { code: 'ru', label: '🇷🇺 RU', name: 'Русский' },
  { code: 'ar', label: '🇸🇦 AR', name: 'العربية' },
  { code: 'ja', label: '🇯🇵 JA', name: '日本語' },
  { code: 'zh', label: '🇨🇳 ZH', name: '中文' },
  { code: 'ko', label: '🇰🇷 KO', name: '한국어' },
  { code: 'nl', label: '🇳🇱 NL', name: 'Nederlands' },
];

function parseVideoTitle(rawTitle, uploader) {
  // Try "Artist - Title" or "Title - Artist" pattern
  const dash = rawTitle.match(/^(.+?)\s*[-–—]\s*(.+?)(?:\s*[\(\[].*)?$/);
  if (dash) return { artist: dash[1].trim(), title: dash[2].trim() };
  return { artist: uploader || '', title: rawTitle.replace(/[\(\[].*?[\)\]]/g, '').trim() };
}

function useFavorites() {
  const [favs, setFavs] = useState([]);
  useEffect(() => {
    try { setFavs(JSON.parse(localStorage.getItem('hepardon_favs') || '[]')); } catch {}
  }, []);
  const toggle = (v) => {
    setFavs(prev => {
      const exists = prev.find(f => f.id === v.id);
      const next = exists ? prev.filter(f => f.id !== v.id) : [v, ...prev].slice(0, 50);
      localStorage.setItem('hepardon_favs', JSON.stringify(next));
      return next;
    });
  };
  const isFav = (id) => favs.some(f => f.id === id);
  return { toggle, isFav };
}

// Load YouTube IFrame API once globally
let _ytApiCallbacks = [];
function loadYTAPI() {
  return new Promise(resolve => {
    if (window.YT?.Player) { resolve(window.YT); return; }
    _ytApiCallbacks.push(resolve);
    if (_ytApiCallbacks.length === 1) {
      window.onYouTubeIframeAPIReady = () => {
        _ytApiCallbacks.forEach(cb => cb(window.YT));
        _ytApiCallbacks = [];
      };
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  });
}

export default function VideoPlayer({ videoId, title, uploader, onClose, onFav, isFav, url }) {
  const { toggle: toggleFav, isFav: isInFav } = useFavorites();
  const [showDl, setShowDl] = useState(false);
  const [dlTab, setDlTab] = useState('audio');
  const [selected, setSelected] = useState('MP3');
  const [dlStatus, setDlStatus] = useState('');
  const [dlError, setDlError] = useState('');
  const [progress, setProgress] = useState(0);
  const [dlUrl, setDlUrl] = useState('');
  const [queue, setQueue] = useState([{ id: videoId, title, uploader, url, thumbnail: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` }]);
  const [curIdx, setCurIdx] = useState(0);
  const [emotion, setEmotion] = useState('');
  const [loadingPl, setLoadingPl] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);
  const [lyrics, setLyrics] = useState('');
  const [lyricsLang, setLyricsLang] = useState(null);
  const [translation, setTranslation] = useState('');
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [lyricsError, setLyricsError] = useState('');

  const pollRef = useRef(null);
  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const playerReadyRef = useRef(false);
  const queueRef = useRef(queue);
  const curIdxRef = useRef(0);
  const fetchPlaylistForRef = useRef(null);
  const goNextRef = useRef(null);

  const curVideo = queue[curIdx] || queue[0];
  const videoUrl = curVideo.url || `https://www.youtube.com/watch?v=${curVideo.id}`;

  useEffect(() => { queueRef.current = queue; }, [queue]);

  const fetchLyrics = async (songTitle, songUploader) => {
    setLyricsLoading(true);
    setLyricsError('');
    setLyrics('');
    setTranslation('');
    setLyricsLang(null);
    const { artist, title } = parseVideoTitle(songTitle, songUploader);
    try {
      const r = await axios.get(`${api}/lyrics`, { params: { artist, title }, timeout: 10000 });
      if (r.data.found) {
        setLyrics(r.data.lyrics);
      } else {
        setLyricsError('Paroles introuvables pour cette chanson.');
      }
    } catch {
      setLyricsError('Impossible de charger les paroles.');
    }
    setLyricsLoading(false);
  };

  const translateLyrics = async (lang, currentLyrics, songTitle, songUploader) => {
    if (!currentLyrics) return;
    setTranslateLoading(true);
    setTranslation('');
    try {
      const r = await axios.post(`${api}/lyrics/translate`, {
        lyrics: currentLyrics, lang,
        title: songTitle, artist: songUploader || ''
      }, { timeout: 35000 });
      if (r.data.translation) setTranslation(r.data.translation);
    } catch {}
    setTranslateLoading(false);
  };

  const goNext = () => {
    const q = queueRef.current;
    const i = curIdxRef.current;
    if (i + 1 < q.length) {
      const next = i + 1;
      curIdxRef.current = next;
      setCurIdx(next);
      setDlStatus(''); setDlUrl(''); setDlError('');
      const v = q[next];
      if (v && playerRef.current && playerReadyRef.current) {
        playerRef.current.loadVideoById(v.id);
      }
      if (v && fetchPlaylistForRef.current) {
        fetchPlaylistForRef.current(v.title, v.uploader, v.id, v.url);
      }
    }
  };

  // Keep refs fresh every render
  useEffect(() => {
    goNextRef.current = goNext;
    fetchPlaylistForRef.current = fetchPlaylistFor;
  });

  // Reset lyrics when song changes
  useEffect(() => {
    setLyrics('');
    setTranslation('');
    setLyricsLang(null);
    setLyricsError('');
    if (showLyrics) {
      const v = queue[curIdx];
      if (v) fetchLyrics(v.title, v.uploader);
    }
  }, [curIdx]); // eslint-disable-line

  // Init YT player and first playlist on mount
  useEffect(() => {
    fetchPlaylistFor(title, uploader, videoId, url);

    loadYTAPI().then(YT => {
      if (!playerContainerRef.current) return;
      playerRef.current = new YT.Player(playerContainerRef.current, {
        videoId,
        playerVars: { autoplay: 1, rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: () => { playerReadyRef.current = true; },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.ENDED) goNextRef.current();
          },
        },
      });
    });

    return () => {
      clearInterval(pollRef.current);
      try { playerRef.current?.destroy(); } catch {}
    };
  }, []);

  const fetchPlaylistFor = async (songTitle, songUploader, songId, songUrl) => {
    setLoadingPl(true);
    try {
      const r = await axios.post(`${api}/ai/playlist`, { title: songTitle, uploader: songUploader });
      const { emotion: emo, playlist } = r.data;
      if (emo) setEmotion(emo);
      const current = {
        id: songId, title: songTitle, uploader: songUploader, url: songUrl,
        thumbnail: `https://i.ytimg.com/vi/${songId}/hqdefault.jpg`,
      };
      setQueue(prev => {
        const played = prev.slice(0, curIdxRef.current);
        const newSuggestions = playlist.filter(v => v.id !== songId && !played.find(p => p.id === v.id));
        const updated = [...played, current, ...newSuggestions];
        queueRef.current = updated;
        return updated;
      });
    } catch {}
    setLoadingPl(false);
  };

  const playAt = (idx) => {
    const v = queue[idx];
    setCurIdx(idx);
    curIdxRef.current = idx;
    setShowDl(false);
    setDlStatus(''); setDlUrl(''); setDlError('');
    if (v && playerRef.current && playerReadyRef.current) {
      playerRef.current.loadVideoById(v.id);
    }
    if (v) fetchPlaylistFor(v.title, v.uploader, v.id, v.url);
  };

  const startDownload = async () => {
    clearInterval(pollRef.current);
    setDlStatus('downloading'); setProgress(0); setDlUrl(''); setDlError('');
    const format = selected.toLowerCase().split(' ')[0];
    const quality = selected.toLowerCase().includes('p') ? selected.toLowerCase().split(' ')[1] : undefined;
    try {
      const r = await axios.post(`${api}/download`, { url: videoUrl, format, quality, title: curVideo.title });
      const jid = r.data.jobId;
      if (!jid) { setDlStatus('error'); setDlError('Réponse invalide du serveur'); return; }
      pollRef.current = setInterval(async () => {
        try {
          const p = await axios.get(`${api}/download/progress/${jid}`);
          setProgress(p.data.progress || 0);
          if (p.data.status === 'done') {
            clearInterval(pollRef.current);
            setDlStatus('done');
            setDlUrl(p.data.downloadUrl);
          }
          if (p.data.status === 'error') {
            clearInterval(pollRef.current);
            setDlStatus('error');
            setDlError('Téléchargement échoué — vidéo peut-être protégée.');
          }
        } catch { clearInterval(pollRef.current); setDlStatus('error'); setDlError('Connexion perdue.'); }
      }, 800);
    } catch (e) {
      setDlStatus('error');
      setDlError(e?.response?.data?.error || 'Impossible de contacter le serveur.');
    }
  };

  const triggerDownload = () => {
    window.open(`https://hepardon.cedlinux.fr${dlUrl}`, '_blank');
  };

  const openNotube = () => {
    // Envoie l'URL de la vidéo DIRECTEMENT à noTube → page de conversion MP3/MP4 de cette vidéo
    const target = 'https://notube.lol/fr/youtube-app-379?keyword=' + encodeURIComponent(videoUrl || '');
    try { if (videoUrl) navigator.clipboard.writeText(videoUrl); } catch (_) {}
    const a = document.createElement('a');
    a.href = target; a.target = '_blank'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* YouTube IFrame API player — no key needed, loadVideoById() switches tracks */}
      <div style={{ background: '#000', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border)' }}>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
          <div
            ref={playerContainerRef}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          />
        </div>
      </div>

      {/* Title + TTS + emotion + buttons */}
      <div style={{ padding: '10px 4px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4 }}>
              {curVideo.title}
            </div>
            {curVideo.uploader && (
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>👤 {curVideo.uploader}</div>
            )}
          </div>
          <TTSBar text={`${curVideo.title}${curVideo.uploader ? ` par ${curVideo.uploader}` : ''}`} lang="fr" inline />
        </div>

        {emotion && (
          <div style={{ fontSize: 11, color: 'var(--purple)', fontStyle: 'italic' }}>
            🎭 Émotion : {emotion}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => { setShowDl(!showDl); setDlStatus(''); setDlUrl(''); setDlError(''); }}
            style={{ flex: 1, background: showDl ? 'var(--gradient)' : 'var(--bg3)', border: showDl ? 'none' : '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            ⬇️ {showDl ? 'Masquer' : 'Télécharger'}
          </button>
          <button
            onClick={() => {
              const next = !showLyrics;
              setShowLyrics(next);
              if (next && !lyrics && !lyricsLoading) fetchLyrics(curVideo.title, curVideo.uploader);
            }}
            style={{ flex: 1, background: showLyrics ? 'linear-gradient(135deg,#06b6d4,#7c3aed)' : 'var(--bg3)', border: showLyrics ? 'none' : '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            🎤 Paroles
          </button>
          {onFav && (
            <button
              onClick={onFav}
              style={{ background: isFav ? 'rgba(255,200,0,0.15)' : 'var(--bg3)', border: isFav ? '1px solid gold' : '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', color: isFav ? 'gold' : 'var(--text2)', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
            >
              {isFav ? '⭐' : '☆'}
            </button>
          )}
          <button
            onClick={onClose}
            style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', color: 'var(--text2)', cursor: 'pointer', fontSize: 14 }}
          >
            ✕
          </button>
        </div>

        {/* Download panel — via noTube (téléchargement direct yt-dlp désactivé, il ne marchait pas) */}
        {showDl && (
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
            <button className="download-btn" onClick={openNotube} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              ⬇️ Télécharger (MP3 / MP4) via noTube
            </button>
            <div style={{ fontSize: 12, color: 'var(--text2)', textAlign: 'center', marginTop: 8, lineHeight: 1.5 }}>
              Tu arrives <b>directement</b> sur la conversion de cette vidéo — choisis MP3 ou MP4.
            </div>
          </div>
        )}

        {/* Lyrics panel */}
        {showLyrics && (
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>🎤 Paroles</div>

            {/* Language chips — horizontal scroll */}
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 10, scrollbarWidth: 'none' }}>
              {LANGUAGES.map(l => (
                <button
                  key={l.code}
                  onClick={() => {
                    setLyricsLang(l.code);
                    translateLyrics(l.code, lyrics, curVideo.title, curVideo.uploader);
                  }}
                  style={{
                    flexShrink: 0, padding: '5px 12px', borderRadius: 99, whiteSpace: 'nowrap',
                    border: lyricsLang === l.code ? 'none' : '1px solid var(--border)',
                    background: lyricsLang === l.code ? 'linear-gradient(135deg,#06b6d4,#7c3aed)' : 'var(--bg)',
                    color: lyricsLang === l.code ? '#fff' : 'var(--text2)',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* Loading paroles */}
            {lyricsLoading && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text2)', fontSize: 12 }}>
                <span className="loader" style={{ width: 16, height: 16 }} />{' '}Recherche des paroles...
              </div>
            )}

            {/* Error */}
            {lyricsError && !lyricsLoading && (
              <div style={{ color: 'var(--text2)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>
                🔍 {lyricsError}
              </div>
            )}

            {/* Translation */}
            {lyrics && !lyricsLoading && (
              <div>
                {translateLoading && (
                  <div style={{ textAlign: 'center', padding: '10px 0', color: 'var(--text2)', fontSize: 12, marginBottom: 8 }}>
                    <span className="loader" style={{ width: 14, height: 14 }} />{' '}Traduction IA en cours...
                  </div>
                )}
                {translation && !translateLoading && (
                  <div style={{ marginBottom: 14, padding: 12, background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.3)', borderRadius: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {LANGUAGES.find(l => l.code === lyricsLang)?.name || lyricsLang}
                      </div>
                      <TTSBar text={translation} lang={lyricsLang} inline />
                    </div>
                    <div className="lyrics-text" style={{ color: 'var(--cyan)', fontSize: 13 }}>{translation}</div>
                  </div>
                )}

                {/* Original lyrics */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ fontSize: 10, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Paroles originales</div>
                    <TTSBar text={lyrics} lang="auto" inline />
                  </div>
                  <div className="lyrics-text" style={{ fontSize: 13 }}>{lyrics}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Playlist queue */}
        {loadingPl && queue.length <= 1 && (
          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text2)', padding: '12px 0' }}>
            <span className="loader" style={{ width: 14, height: 14 }} />
            {' '}L'IA analyse l'émotion et prépare ta playlist...
          </div>
        )}

        {queue.length > 1 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>🎭 Playlist émotionnelle</span>
              {loadingPl && <span className="loader" style={{ width: 12, height: 12 }} />}
              <span style={{ marginLeft: 'auto', fontWeight: 400 }}>{queue.length - 1} titres</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {queue.map((v, i) => (
                <div
                  key={v.id + '-' + i}
                  onClick={() => playAt(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                    background: curIdx === i ? 'var(--gradient)' : 'var(--bg3)',
                    border: curIdx === i ? 'none' : '1px solid var(--border)',
                    borderRadius: 10, cursor: 'pointer', opacity: i < curIdx ? 0.4 : 1,
                  }}
                >
                  <img
                    src={v.thumbnail || `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
                    alt=""
                    style={{ width: 52, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: curIdx === i ? '#fff' : 'var(--text)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {curIdx === i && '▶ '}{v.title}
                    </div>
                    {v.uploader && (
                      <div style={{ fontSize: 10, color: curIdx === i ? 'rgba(255,255,255,0.75)' : 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.uploader}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); toggleFav(v); }}
                    style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', flexShrink: 0, color: isInFav(v.id) ? 'gold' : (curIdx === i ? 'rgba(255,255,255,0.6)' : 'var(--text2)'), padding: '0 4px' }}
                  >
                    {isInFav(v.id) ? '⭐' : '☆'}
                  </button>
                  {v.duration_string && (
                    <div style={{ fontSize: 10, color: curIdx === i ? 'rgba(255,255,255,0.7)' : 'var(--text2)', flexShrink: 0 }}>
                      {v.duration_string}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
